import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";

interface ImageCropModalProps {
    isOpen: boolean;
    file: File | null;
    onClose: () => void;
    onConfirm: (file: File) => Promise<void>;
    title: string;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
    isOpen,
    file,
    onClose,
    onConfirm,
    title,
}) => {
    const { t } = useTranslation("global");
    const [preview, setPreview] = useState("");
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 50, y: 50 });
    const [saving, setSaving] = useState(false);
    const viewportRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{
        x: number;
        y: number;
        startX: number;
        startY: number;
    } | null>(null);

    useEffect(() => {
        if (!file) {
            setPreview("");
            return;
        }

        const url = URL.createObjectURL(file);
        setPreview(url);
        setZoom(1);
        setPosition({ x: 50, y: 50 });
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const startDrag = (event: React.PointerEvent<HTMLDivElement>): void => {
        dragRef.current = {
            x: position.x,
            y: position.y,
            startX: event.clientX,
            startY: event.clientY,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const moveDrag = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!dragRef.current || !viewportRef.current) {
            return;
        }

        const rect = viewportRef.current.getBoundingClientRect();

        setPosition({
            x: Math.max(
                0,
                Math.min(
                    100,
                    dragRef.current.x +
                        ((event.clientX - dragRef.current.startX) /
                            rect.width) *
                            100
                )
            ),
            y: Math.max(
                0,
                Math.min(
                    100,
                    dragRef.current.y +
                        ((event.clientY - dragRef.current.startY) /
                            rect.height) *
                            100
                )
            ),
        });
    };

    const confirm = async (): Promise<void> => {
        if (!preview) {
            return;
        }

        setSaving(true);
        try {
            const image = new Image();
            image.src = preview;
            await image.decode();
            const size = 512;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const context = canvas.getContext("2d");
            if (!context) {
                return;
            }

            const scale = Math.max(size / image.width, size / image.height) * zoom;
            const width = image.width * scale;
            const height = image.height * scale;
            context.drawImage(
                image,
                (size - width) * (position.x / 100),
                (size - height) * (position.y / 100),
                width,
                height
            );

            const blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, "image/webp", 0.9)
            );
            if (blob) {
                await onConfirm(new File([blob], "logo.webp", { type: "image/webp" }));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            maxWidth="sm"
            onSubmit={confirm}
            submitLabel={t("account_avatar.save")}
            isSubmitting={saving}
        >
            <div className="space-y-4">
                <div
                    ref={viewportRef}
                    className="relative mx-auto h-64 w-64 touch-none cursor-move overflow-hidden rounded-full bg-slate-100"
                    onPointerDown={startDrag}
                    onPointerMove={moveDrag}
                    onPointerUp={() => {
                        dragRef.current = null;
                    }}
                    onPointerCancel={() => {
                        dragRef.current = null;
                    }}
                >
                    {preview && (
                        <div
                            className="absolute inset-0 rounded-full bg-cover bg-no-repeat"
                            style={{
                                backgroundImage: `url("${preview}")`,
                                backgroundPosition: `${position.x}% ${position.y}%`,
                                backgroundSize: `${zoom * 100}%`,
                            }}
                        />
                    )}
                </div>
                <label className="block text-sm font-semibold">
                    <div>{t("account_avatar.zoom")}</div>
                    <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.05"
                        value={zoom}
                        onChange={(event) => setZoom(Number(event.target.value))}
                        className="mt-2 w-full accent-primary"
                    />
                </label>
                <div className="text-center text-xs text-slate-500 dark:text-zinc-400">
                    {t("account_avatar.drag_hint")}
                </div>
            </div>
        </Modal>
    );
};
